import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { type FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import { type CICaseDetail } from 'components/CriticalIncident/interfaces';
import { Spinner } from 'components/Shared';
import { LabelValue } from 'components/Shared/LabelValue';
import { CICard } from '../../Shared/CICard/CICard';
import { InterventionCard } from './InterventionCard';
import { useGetAllReferences } from 'api/queries/referencesQuery';

export const Overview: FunctionComponent<{ ciCase: CICaseDetail }> = ({ ciCase }) => {
    const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });
    const { data: references, isLoading: referencesLoading } = useGetAllReferences();

    const accountName = [ciCase.organization?.company, ciCase.organization?.branch, ciCase.organization?.division].filter(Boolean).join(', ');

    return translationsReady ? (
        <Container maxWidth="lg">
            <CICard className='case_summary_paper'>
                <h3>{t('account')}</h3>
                <Grid container>
                    <Grid item xs={12} sm={6}>
                        <LabelValue label={t('client')} value={accountName} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LabelValue label={t('industry')} value={referencesLoading ? undefined : references?.industries?.find(i => i.value === `${ciCase.organization?.industryCode}`)?.title} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LabelValue label={t('case_id')} value={ciCase.externalCaseId} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LabelValue label={t('work_order_id')} value={ciCase.externalWorkOrderId} />
                    </Grid>
                </Grid>
            </CICard>

            <CICard className='case_summary_paper'>
              <h3>{t('incident')}</h3>
              <Grid container>
                <Grid item xs={12} sm={6}>
                  <LabelValue label={t('service')} value={referencesLoading ? undefined : references?.services?.find(s => s.value === ciCase.incident?.serviceCode)?.title} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LabelValue label={t('incident_category')} value={referencesLoading ? undefined : references?.issues?.find(c => c.value === ciCase.incident?.presentingIssueCode)?.title} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LabelValue label={t('number_of_impacted_individuals')} value={ciCase.incident?.numberOfEmployee} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LabelValue label={t('description')} value={ciCase.incident?.description} />
                </Grid>
              </Grid>
            </CICard>

            <InterventionCard ciCase={ciCase} />

            <CICard className='case_summary_paper'>
                <h3>{t('contacts')}</h3>
                <h4>{t('onsite')}</h4>
                <Grid container>
                    <Grid item xs={12} sm={6}>
                        <LabelValue label={t('name')} value={ciCase?.contacts?.onSite?.name ?? undefined} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LabelValue label={t('email')} value={ciCase?.contacts?.onSite?.email ?? undefined} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LabelValue label={t('phone')} value={ciCase?.contacts?.onSite?.phone ?? undefined} />
                    </Grid>
                </Grid>

                <h4>{t('requester')}</h4>
                <Grid container>
                    <Grid item xs={12} sm={6}>
                        <LabelValue label={t('name')} value={ciCase?.contacts?.requestor?.name ?? undefined} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LabelValue label={t('email')} value={ciCase?.contacts?.requestor?.email ?? undefined} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LabelValue label={t('phone')} value={ciCase?.contacts?.requestor?.phone ?? undefined} />
                    </Grid>
                </Grid>
            </CICard>
        </Container>
    ) : (
        <Spinner fillParent />
    );
};
